import { useCallback, useEffect, useState } from "react";
import { Link, NavLink, Outlet, useParams } from "react-router-dom";
import { Award, Building2, CalendarClock, ClipboardList, CreditCard, FileText, Handshake, Home, LayoutDashboard, ListChecks, LogOut, Menu, Settings as SettingsIcon, ShieldCheck } from "lucide-react";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { FullPageLoader, Logo } from "@/components/Logo";
import { WorkspaceSwitcher } from "./WorkspaceSwitcher";
import { SampleBanner } from "./SampleBanner";
import { NotificationBell } from "./NotificationBell";
import { useAuth } from "@/context/AuthContext";
import { api, errorMessage } from "@/lib/api";

const NAV = [
  ["", "Overview", LayoutDashboard],
  ["properties", "Properties", Home],
  ["documents", "Documents", FileText],
  ["actions", "Actions", ListChecks],
  ["deadlines", "Deadlines", CalendarClock],
  ["forms", "Forms", ClipboardList],
  ["ip", "Trademarks & patents", Award],
  ["settings", "Settings", SettingsIcon],
  ["compliance", "Business compliance", Building2],
  ["billing", "Plan & billing", CreditCard],
  ["affiliate", "Affiliate program", Handshake],
];

const SideNav = ({ wid, workspaces, workspace, onNavigate }) => {
  const { user, logout } = useAuth();
  return (
    <div className="flex h-full flex-col bg-navy text-white">
      <div className="px-5 pt-6 pb-5"><Logo light to="/app" testId="app-logo" /></div>
      <div className="px-3"><WorkspaceSwitcher workspaces={workspaces} current={workspace} /></div>
      <nav className="mt-6 flex-1 space-y-1 px-3">
        {[...NAV, ...(user?.is_admin ? [["admin", "Affiliates admin", ShieldCheck]] : [])].map(([path, label, Icon]) => (
          <NavLink key={label} to={`/app/w/${wid}${path ? `/${path}` : ""}`} end={!path} onClick={onNavigate}
            data-testid={`nav-${path || "overview"}-link`}
            className={({ isActive }) => `flex items-center gap-3 rounded-md px-3 py-2.5 text-sm transition-colors ${isActive ? "bg-white/10 text-white shadow-[inset_3px_0_0_#C5A059]" : "text-white/70 hover:bg-white/5 hover:text-white"}`}>
            <Icon className="h-4 w-4" /> {label}
          </NavLink>
        ))}
      </nav>
      <div className="border-t border-white/10 p-4">
        <p className="truncate text-sm font-medium" data-testid="sidebar-user-name">{user?.name}</p>
        <p className="truncate text-xs text-white/50" data-testid="sidebar-user-email">{user?.email}</p>
        <button onClick={logout} data-testid="signout-button" className="mt-3 inline-flex items-center gap-2 text-xs text-white/70 hover:text-gold">
          <LogOut className="h-3.5 w-3.5" /> Sign out
        </button>
      </div>
    </div>
  );
};

export default function AppShell() {
  const { wid } = useParams();
  const [workspace, setWorkspace] = useState(null);
  const [workspaces, setWorkspaces] = useState([]);
  const [properties, setProperties] = useState([]);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);
  const [desktop, setDesktop] = useState(() => window.matchMedia("(min-width: 1024px)").matches);
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const on = (e) => setDesktop(e.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);

  const load = useCallback(async () => {
    try {
      const [w, list, props] = await Promise.all([api.get(`/workspaces/${wid}`), api.get("/workspaces"), api.get(`/workspaces/${wid}/properties`)]);
      setWorkspace(w.data.workspace);
      setWorkspaces(list.data.workspaces);
      setProperties(props.data.properties);
      setError("");
      if (!w.data.workspace.is_sample) localStorage.setItem("evr_last_ws", wid);
    } catch (e) {
      setError(errorMessage(e));
    }
  }, [wid]);

  useEffect(() => { setWorkspace(null); load(); }, [load]);

  if (error) return (
    <div data-testid="workspace-not-found" className="grid min-h-screen place-items-center bg-paper px-6 text-center">
      <div><h1 className="text-2xl font-bold text-navy">{error}</h1>
        <Link to="/app" data-testid="workspace-not-found-home-link" className="mt-4 inline-block text-navy underline underline-offset-4">Go to my workspace</Link></div>
    </div>
  );
  if (!workspace) return <FullPageLoader />;

  const nav = <SideNav wid={wid} workspaces={workspaces} workspace={workspace} onNavigate={() => setOpen(false)} />;
  return (
    <div className="min-h-screen bg-paper lg:pl-64">
      <aside className="fixed inset-y-0 left-0 hidden w-64 lg:block">{nav}</aside>
      <header className="sticky top-0 z-30 flex items-center justify-between border-b bg-navy px-4 py-3 lg:hidden">
        <Logo light to="/app" testId="mobile-logo" />
        <div className="flex items-center gap-1">
          {!desktop && <NotificationBell workspace={workspace} light />}
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger data-testid="mobile-menu-button" className="rounded-md p-2 text-white hover:bg-white/10"><Menu className="h-5 w-5" /></SheetTrigger>
            <SheetContent side="left" className="w-72 border-0 p-0"><SheetTitle className="sr-only">Menu</SheetTitle>{nav}</SheetContent>
          </Sheet>
        </div>
      </header>
      {desktop && (
        <div className="sticky top-0 z-30 flex h-14 items-center justify-end border-b bg-paper/90 px-8 backdrop-blur">
          <NotificationBell workspace={workspace} />
        </div>
      )}
      {workspace.is_sample && <SampleBanner workspace={workspace} />}
      <main className="mx-auto max-w-6xl px-4 py-8 sm:px-8 sm:py-10">
        <Outlet context={{ workspace, reloadWorkspace: load, properties, reloadProperties: load }} />
      </main>
    </div>
  );
}
